/**
 * The fixed list of capabilities. Roles are named sets of these; screens and
 * endpoints check capabilities, never role names. See docs/design/07-roles-and-capabilities.md.
 */
export const CAPABILITY_GROUPS = {
    orders: {
        'orders.view': 'View orders',
        'orders.edit': 'Edit addresses, notes, hold, assign warehouse',
        'orders.release': 'Release orders and create fulfilments',
        'orders.cancel': 'Cancel orders',
        'orders.resolve': 'Clear needs-check flags',
    },
    shops: {
        'shops.view': 'View shops and work views',
        'shops.import': 'Import orders from 3x',
        'shops.manage': 'Priority, shipment date, packing instructions, shipping rates, archive',
        'shops.storefront': 'Storefront set-up: theme, domains, pages, assortment and shop prices',
    },
    batches: {
        'batches.create': 'Create picking batches',
        'batches.manage': 'Cancel, regenerate, staging locations',
    },
    floor: {
        'floor.pick': 'Pick screens, confirm picks, report shortages',
        'floor.pack': 'Pack screen, enter packages, confirm hand-over',
        'floor.scan_print': 'Scan-to-print and reprint labels',
        'floor.returns': 'Returned-parcel scan, restock or scrap',
    },
    shipping: {
        'shipping.book': 'Book carriers, choose service, override routing',
        'shipping.cancel': 'Cancel booked shipments',
        'shipping.manage': 'Carrier accounts and routing rules',
    },
    stock: {
        'stock.view': 'Stock, ledger, locations',
        'stock.receive': 'Receive against PO or transfer, put away',
        'stock.adjust': 'Adjustments and scrap',
        'stock.count': 'Count sessions',
        'stock.locations': 'Create, upload, print location labels',
        'stock.transfer': 'Transfers and replenishment from remote storage',
    },
    catalogue: {
        'catalogue.view': 'View products, categories and vendors',
        'catalogue.edit': 'Create and edit products, images, categories and vendors',
    },
    customers: {
        'customers.view': 'View storefront customers and groups',
        'customers.edit': 'Create, edit, approve, invite and disable customers; groups',
        'customers.import': 'Import customers from CSV or XLSX',
    },
    handout: {
        'handout.scan': 'Hand-out scanner: look up, hand out, list and today',
        'handout.recall': 'Take hand-outs back within the recall window',
        'handout.edit': 'Hand-out editor: overview, all orders, history, log',
        'handout.reports': 'Hand-out reports (XLSX, CSV) and presets',
        'handout.manage': 'Zones, hand-out users and their scope, shared presets',
    },
    gaveuniverset: {
        'gu.view': 'Gaveuniverset: seasons, assortments, buyers and their shops',
        'gu.manage': 'Gaveuniverset: edit seasons, fees, shipping and assortments',
        'gu.buyers': 'Gaveuniverset: create and edit buyers, log in as a buyer',
    },
    xmas: {
        'xmas.view': 'Christmas packing: plans, batches, packages',
        'xmas.pack': 'Christmas packing: print, pack, book packages by scanning them, and cancel those bookings',
        'xmas.manage': 'Christmas packing: create plans, add shops, set the receiver',
    },
    purchasing: {
        'purchasing.view': 'View purchase orders',
        'purchasing.edit': 'Create and edit purchase orders and suppliers',
    },
    finance: {
        'finance.view': 'Finance work lists and invoice state',
        'finance.invoice': 'Create, approve, send invoices and credit notes',
        'finance.settings': 'Account mapping and Dinero connection',
    },
    printing: {
        'printing.print': 'Print and reprint to printers in my warehouses',
        'printing.manage': 'Queue management, agents, auto-print rules',
    },
    settings: {
        'users.manage': 'Manage users',
        'roles.manage': 'Manage roles and the capability matrix',
        'settings.manage': 'Warehouses, stations, packaging, feed settings',
        'audit.view': 'View the audit log',
    },
};
export const CAPABILITIES = Object.freeze(Object.values(CAPABILITY_GROUPS).flatMap((g) => Object.keys(g)));
export function isCapability(value) {
    return CAPABILITIES.includes(value);
}
export function capabilityLabel(cap) {
    for (const group of Object.values(CAPABILITY_GROUPS)) {
        const label = group[cap];
        if (label)
            return label;
    }
    return cap;
}
/** Seed roles. The admin role is locked and always holds every capability. */
export const DEFAULT_ROLES = {
    admin: { label: 'Admin', caps: [...CAPABILITIES], system: true },
    warehouse_manager: {
        label: 'Warehouse manager',
        system: true,
        caps: [
            'orders.view', 'orders.edit', 'orders.release', 'orders.cancel', 'orders.resolve',
            'shops.view', 'shops.import', 'shops.manage',
            'batches.create', 'batches.manage',
            'floor.pick', 'floor.pack', 'floor.scan_print', 'floor.returns',
            'shipping.book', 'shipping.cancel', 'shipping.manage',
            'stock.view', 'stock.receive', 'stock.adjust', 'stock.count', 'stock.locations', 'stock.transfer',
            'purchasing.view', 'purchasing.edit',
            'printing.print', 'printing.manage',
            'finance.view',
        ],
    },
    picker: {
        label: 'Picker',
        system: false,
        caps: ['floor.pick', 'floor.scan_print', 'orders.view', 'stock.view', 'printing.print'],
    },
    packer: {
        label: 'Packer',
        system: false,
        caps: [
            'floor.pick', 'floor.pack', 'floor.scan_print', 'floor.returns',
            'orders.view', 'stock.view', 'printing.print', 'shipping.book', 'batches.create',
        ],
    },
    receiver: {
        label: 'Receiver',
        system: false,
        caps: ['stock.view', 'stock.receive', 'stock.locations', 'purchasing.view', 'printing.print'],
    },
    finance: {
        label: 'Finance',
        system: false,
        caps: ['orders.view', 'shops.view', 'finance.view', 'finance.invoice', 'finance.settings'],
    },
    kam: {
        label: 'KAM',
        system: false,
        caps: ['orders.view', 'shops.view', 'stock.view'],
    },
    /** QR2's roles (P2.5): the scanner hands out and takes back; the editor also sees everything and reports. */
    handout_scanner: {
        label: 'Udlevering: scanner',
        system: true,
        caps: ['handout.scan', 'handout.recall'],
    },
    handout_editor: {
        label: 'Udlevering: editor',
        system: true,
        caps: ['handout.scan', 'handout.recall', 'handout.edit', 'handout.reports'],
    },
    /** Everything Christmas packing needs, plans to cancelling bookings, and nothing outside it. */
    xmas_packer: {
        label: 'Julepakning',
        system: true,
        caps: ['xmas.view', 'xmas.pack', 'xmas.manage'],
    },
};
//# sourceMappingURL=capabilities.js.map